// FILE: src/components/InviteManagers.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "../components/ui/PageScaffold";
import "../styles/layout.css";
import VideoLightbox from "../components/ui/VideoLightbox";
import { useAccountManagerLimit } from "../hooks/useAccountManagerLimit";

function fmtDate(d) {
  try {
    const x = typeof d === "string" ? new Date(d) : d?.toDate ? d.toDate() : new Date(d);
    return isNaN(x.getTime()) ? "—" : x.toLocaleString();
  } catch {
    return "—";
  }
}
const tryParseJSON = (t) => { try { return JSON.parse(t); } catch { return null; } };
async function fetchJSON(url, options) {
  const r = await fetch(url, options);
  const txt = await r.text();
  const j = tryParseJSON(txt) || {};
  return { ok: r.ok, body: j, raw: txt, status: r.status };
}

export default function InviteManagers() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => onAuthStateChanged(auth, setMe), []);

  const { loading: limitLoading, limit, count: usedCount, remaining } = useAccountManagerLimit(me?.uid);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false); // prevent multi-submit
  const [showLimitModal, setShowLimitModal] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    sendOtac: true,
    passwordHint: "",
    delivery: "upon-death",  // "upon-death" | "immediate" | "scheduled"
    deliverAt: "",
  });
  const firstFieldRef = useRef(null);

  const [profileName, setProfileName] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [accessEnabled, setAccessEnabled] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);

  const atLimit = !limitLoading && remaining <= 0;

  useEffect(() => {
    if (!atLimit) setShowLimitModal(false);
  }, [atLimit]);


  // load list
  useEffect(() => {
    if (!me?.uid) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const u = auth.currentUser;
        const tok = u ? await u.getIdToken() : "";
        const { ok, body, raw } = await fetchJSON("/api/accountManagers/listMine", {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!ok) throw new Error(body.error || raw || "Failed to load managers");
        setItems(Array.isArray(body.items) ? body.items : []);
      } catch (e) {
        setErr(e.message || "Failed to load account managers");
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [me?.uid]);

  // owner name
  useEffect(() => {
    if (!me?.uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", me.uid));
        const d = snap.exists() ? (snap.data() || {}) : {};
        const n =
          d.name ||
          d.fullName ||
          d.displayName ||
          [d.firstName, d.lastName].filter(Boolean).join(" ") ||
          me.displayName ||
          me.email ||
          "";
        setProfileName(String(n || "").trim());
      } catch {}
    })();
  }, [me?.uid]);

  const ownerNameForMsg =
    (typeof profileName === "string" && profileName.trim()) ||
    (me && (me.displayName || me.name || me.email)) ||
    "";

    const openUpgradeModalLink = () => {
    navigate("/profile?upgrade=1");
  };

  // default message
  useEffect(() => {
    setForm((f) => {
      if (f.message && f.message.trim()) return f;
      return {
        ...f,
        message:
          `Hi ${((f.name || "").trim() || "there")}, ` +
          (
            ownerNameForMsg
              ? `you’ve been invited to become an Account Manager for “${ownerNameForMsg}”. `
              : `you’ve been invited to become an Account Manager. `
          ) +
          `You’ll have read-only access to help carry out final wishes with care, privacy, and respect.`,
      };
    });
  }, [ownerNameForMsg]);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function closeForm() { if (!saving) { setShowForm(false); setEditing(null); } }

  async function reloadList() {
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";
      const { ok, body } = await fetchJSON("/api/accountManagers/listMine", {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (ok) setItems(Array.isArray(body.items) ? body.items : []);
    } catch {}
  }

  async function applyAccess(inviteId, next, currentEditing) {
    const u = auth.currentUser;
    const tok = u ? await u.getIdToken() : "";

    async function mirrorInviteAccess() {
      try {
        await setDoc(
          doc(db, "accountManagers", inviteId),
          { accessEnabled: !!next, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch {}
    }

    const r1 = await fetchJSON("/api/accountManagers/setAccess", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ id: inviteId, accessEnabled: !!next }),
    });
    if (r1.ok && !r1.body.error) { await mirrorInviteAccess(); return true; }

    const r2 = await fetchJSON(next ? "/api/accountManagers/allow" : "/api/accountManagers/deny", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ id: inviteId }),
    });
    if (r2.ok && !r2.body.error) { await mirrorInviteAccess(); return true; }

    try {
      const ownerUid = me?.uid;
      const mUid =
        currentEditing?.managerUid ||
        currentEditing?.uid ||
        currentEditing?.userUid ||
        currentEditing?.userId ||
        currentEditing?.managerUserId ||
        currentEditing?.acceptedBy ||
        "";
      if (ownerUid && mUid) {
        await setDoc(
          doc(db, "accounts", ownerUid, "managers", mUid),
          { accessEnabled: !!next, updatedAt: serverTimestamp() },
          { merge: true }
        );
        await setDoc(
          doc(db, "grants", `${ownerUid}_${mUid}`),
          { ownerUid, managerUid: mUid, active: !!next, readOnly: true, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }
      await mirrorInviteAccess();
      return true;
    } catch {
      return false;
    }
  }

  async function openForm(item = null) {
    setErr("");
    if (item) {
      setEditing(item);
      setForm((f) => ({
        ...f,
        name: item.name ?? item.recipient ?? f.name ?? "",
        email: item.email ?? f.email ?? "",
        message: (item.message && String(item.message)) || f.message || "",
        sendOtac: true,
        passwordHint: item.secretNote || item.passwordHint || "",
        delivery:
          item.delivery === "immediate" || item.delivery === "scheduled" || item.delivery === "upon-death"
            ? item.delivery
            : item.delivery === "upon_death"
            ? "upon-death"
            : "upon-death",
        deliverAt:
          item.delivery === "scheduled"
            ? item.deliverAt?.seconds
              ? new Date(item.deliverAt.seconds * 1000).toISOString().slice(0, 16)
              : item.deliverAt || ""
            : "",
      }));
      setAccessEnabled(!!item.accessEnabled);

      try {
        const ownerUid = auth.currentUser && auth.currentUser.uid;
        const managerUid =
          item.managerUid || item.uid || item.userUid || item.userId || item.managerUserId || item.acceptedBy;
        if (ownerUid && managerUid) {
          const gref = doc(db, "grants", `${ownerUid}_${managerUid}`);
          const gsnap = await getDoc(gref);
          if (gsnap.exists()) {
            const g = gsnap.data() || {};
            setAccessEnabled(!!g.active);
          }
        }
      } catch {}
    } else {
      setEditing(null);
      setForm((f) => ({
        name: "",
        email: "",
        message: f.message || "",
        sendOtac: true,
        passwordHint: "",
        delivery: "upon-death",
        deliverAt: "",
      }));
      setAccessEnabled(false);
    }
    setShowForm(true);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return; // guard re-entrancy
    setSaving(true);
    setErr("");
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";

      const includeSecret = !!(form.passwordHint && form.passwordHint.trim());
      const secretNote = includeSecret ? form.passwordHint.trim() : "";

      const isScheduled = form.delivery === "scheduled";
      let deliverAtIso = "";
      if (isScheduled) {
        if (!form.deliverAt) throw new Error("Please choose a date & time.");
        const d = new Date(form.deliverAt);
        if (isNaN(d.getTime())) throw new Error("Please choose a valid date & time.");
        if (d.getTime() < Date.now() + 60 * 1000)
          throw new Error("Scheduled time must be at least 1 minute in the future.");
        deliverAtIso = d.toISOString();
      }

      const appOrigin = window.location.origin;
      const verifyUrl = `${appOrigin}/accept-invitation`;
      const verifyAliases = [`${appOrigin}/am/accept`, `${appOrigin}/invite`];

      const creating = !editing?.id;
      const basePayload = {
        id: editing?.id || "",
        name: form.name,
        email: form.email,
        message: form.message,
        includeSecret,
        secretNote,
        otacDays: 7,
        delivery: form.delivery,
        deliverAt: isScheduled ? deliverAtIso : "",
        verifyUrl,
        verifyAliases,
        // Without this the server defaults to "business", so a personal account
        // holder's manager was filed under Managed Business Accounts and the
        // invite email called them a Business Account Manager.
        accountContext: "personal",
      };

      if (creating) {
        const createRes = await fetchJSON("/api/accountManagers/upsert", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ ...basePayload, sendOtac: !!form.sendOtac }),
        });
        if (!createRes.ok) throw new Error(createRes.body.error || createRes.raw || "Save failed");
      } else {
        const preferred = await fetchJSON("/api/accountManagers/updateNoEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ ...basePayload }),
        });
        if (!preferred.ok) {
          const fallback = await fetchJSON("/api/accountManagers/upsert", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ ...basePayload, sendOtac: false, suppressEmail: true, updateOnly: true }),
          });
          if (!fallback.ok) throw new Error(fallback.body.error || fallback.raw || "Save failed");
        }
        await applyAccess(editing.id, accessEnabled, editing);
      }

      await reloadList();
      closeForm();
    } catch (e2) {
      const errMsg = e2.message || "Save failed";
      if (errMsg.toLowerCase().includes("already an account manager")) {
        alert(errMsg);
      } else {
        setErr(errMsg);
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleAccessToggle(next) {
    if (!editing?.id) return;
    setAccessBusy(true);
    setErr("");
    try {
      const ok = await applyAccess(editing.id, next, editing);
      if (!ok) throw new Error("Failed to update access");
      setAccessEnabled(!!next);
      setItems((xs) => xs.map((x) => (x.id === editing.id ? { ...x, accessEnabled: !!next } : x)));
      setEditing((e) => (e ? { ...e, accessEnabled: !!next } : e));
    } catch (e) {
      setErr(e.message || "Failed to update access");
    } finally {
      setAccessBusy(false);
    }
  }

  async function handleSendLoginLink() {
    if (!editing?.id) return;
    setErr("");
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";
      const r = await fetchJSON("/api/accountManagers/sendLoginLink", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ id: editing.id }),
      });
      if (r.ok) { alert("Login link sent."); return; }
      const url = `${window.location.origin}/manager-select`;
      await navigator.clipboard.writeText(url);
      alert("Login link copied to clipboard:\n" + url);
    } catch (e) {
      setErr(e.message || "Failed to send link");
    }
  }

  async function handleDelete(item) {
    if (!item?.id) return;
    if (!window.confirm("Delete this account manager invitation?")) return;
    setErr("");
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";

      const r = await fetchJSON("/api/accountManagers/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ id: item.id }),
      });

      if (!r.ok || r.body?.error) {
        const ownerUid = me?.uid;
        const mUid =
          item.managerUid ||
          item.uid ||
          item.userUid ||
          item.userId ||
          item.managerUserId ||
          item.acceptedBy ||
          "";
        try { await deleteDoc(doc(db, "accountManagers", item.id)); } catch {}
        if (ownerUid && mUid) {
          try { await deleteDoc(doc(db, "accounts", ownerUid, "managers", mUid)); } catch {}
          try { await deleteDoc(doc(db, "grants", `${ownerUid}_${mUid}`)); } catch {}
        }
      }

      await reloadList();
    } catch (e) {
      setErr(e.message || "Delete failed");
    }
  }

  const actionsDesktop = (
    <>
      <button className="btn am-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="am-help-ico" />
        Read how it works
      </button>
      <button className="btn am-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="am-help-ico" />
        See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="am-help-row">
      <button className="btn am-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="am-help-ico" />
        Read how it works
      </button>
      <button className="btn am-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="am-help-ico" />
        See how it works
      </button>
    </div>
  );

    function handleAddClick() {
    if (atLimit) {
      setErr("You’ve reached your package limit for account managers.");
      return;
    }
    setErr("");
    setShowForm(true);
  }

  return (
    <>
      <PageScaffold
        title="Account Managers"
        onAdd={handleAddClick}
        heroSrc="/lastwish-account-managers.jpg"
        heroAlt="Account Managers"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
      >
        <div className="am-list">
          {atLimit && (
            <div
              className="empty-state"
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#7a0b0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                borderRadius: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <strong>Package limit reached.</strong>{" "}
                {limitLoading
                  ? ""
                  : `You’ve invited ${usedCount || 0} of ${limit || 0} account manager${(limit || 0) === 1 ? "" : "s"}.`}
              </div>
              <button className="btn btn-main" type="button" onClick={openUpgradeModalLink}>
                Upgrade package
              </button>
            </div>
          )}
          {err && <div className="empty-state" style={{ color: "#9b1c1c" }}>{err}</div>}
          {loading ? (
            <div className="empty-state">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty-state">No account managers yet.</div>
          ) : (
            items.map((it) => (
              <div key={it.id} className="card am-item">
                <div className="tile am-item-main">
                  <div className="tile-thumb" style={{ display: "grid", placeItems: "center", background: "#003353", borderRadius: "50%", color: "#fff", fontWeight: 700, fontSize: "1rem", width: 44, height: 44, flexShrink: 0 }}>
                    {(it.name || it.recipient || it.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="tile-main">
                    <div className="tile-title">{it.name || it.recipient || "—"}</div>
                    <div className="tile-sub">{it.email || "—"}</div>

                    {/* Access mode line */}
                    <div className="tile-sub" style={{ marginTop: 4 }}>
                      <span style={{ opacity: 0.85 }}>
                        Access mode:&nbsp;
                        {it.delivery === "immediate"
                          ? "Instant"
                          : it.delivery === "scheduled"
                          ? `Scheduled (${fmtDate(it.deliverAt)})`
                          : "Upon death"}
                      </span>
                    </div>

                    {/* Status chips */}
                    <div className="tile-sub" style={{ marginTop: 4 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "2px 8px",
                          borderRadius: 999,
                          border: "1px solid #e5e7eb",
                          background: it.acceptedAt ? "#ecfdf5" : "#f9fafb",
                          color: it.acceptedAt ? "#065f46" : "#374151",
                          marginRight: 6,
                          fontSize: 12,
                        }}
                      >
                        {it.acceptedAt ? "Accepted" : "Pending"}
                      </span>

                      {typeof it.accessEnabled === "boolean" && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "2px 8px",
                            borderRadius: 999,
                            border: "1px solid #e5e7eb",
                            background: it.accessEnabled ? "#ecfdf5" : "#f3f4f6",
                            color: it.accessEnabled ? "#065f46" : "#374151",
                            fontSize: 12,
                          }}
                        >
                          {it.accessEnabled ? "Access enabled" : "Access disabled"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="am-item-actions">
                  <button className="btn" style={{ background: "#f15822", color: "#fff", border: "none" }} onClick={() => openForm(it)}>Edit</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(it)}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </PageScaffold>

      {showForm && (
        <div className="modal-bg" onClick={closeForm}>
          <form
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            aria-busy={saving ? "true" : "false"}
          >
            <h3>{editing ? "Edit Account Manager" : "Add Account Manager"}</h3>

            {/* saving banner */}
            {saving && (
              <div style={{ marginTop: 6, marginBottom: 8, padding: "8px 10px", background: "#fff7ed", border: "1px solid #fde68a", borderRadius: 6, fontSize: 13 }}>
                Saving… please wait
              </div>
            )}

            <div className="modal-body" style={saving ? { opacity: 0.6, pointerEvents: "none" } : undefined}>
              <label className="label">Name</label>
              <input
                ref={firstFieldRef}
                className="input"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                required
                placeholder="Full name"
              />

              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                required
                placeholder="name@example.com"
              />

              <label className="label">Message</label>
              <textarea
                className="input"
                rows={6}
                value={form.message}
                onChange={(e) => setField("message", e.target.value)}
                placeholder="Personal note to include with the invitation"
              />

              <div style={{ marginTop: 10 }}>
                <label
                  className="label"
                  style={{ display: "inline-flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}
                  title="Include a single-use code in the email (recommended)"
                >
                  <input
                    type="checkbox"
                    checked={form.sendOtac}
                    onChange={(e) => setField("sendOtac", e.target.checked)}
                  />
                  Send a one-time access code (recommended)
                </label>
                <div style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>
                  A single-use code is generated and included in the email. The recipient must enter it after clicking “Accept Invitation.”
                </div>
              </div>

              <label className="label" style={{ marginTop: 12 }}>Password hint (optional)</label>
              <textarea
                className="input"
                rows={3}
                value={form.passwordHint}
                onChange={(e) => setField("passwordHint", e.target.value)}
                placeholder="If you want to provide a hint for your master password, enter it here."
              />

              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                <label className="label">Delivery</label>
                <select className="input" value={form.delivery} onChange={(e) => setField("delivery", e.target.value)}>
                  <option value="upon-death">Upon death</option>
                  <option value="immediate">Send immediately</option>
                  <option value="scheduled">Schedule a date & time</option>
                </select>

                {form.delivery === "scheduled" && (
                  <>
                    <label className="label">Deliver at</label>
                    <input
                      type="datetime-local"
                      className="input"
                      value={form.deliverAt}
                      onChange={(e) => setField("deliverAt", e.target.value)}
                      required
                      min={new Date(Date.now() + 2 * 60 * 1000).toISOString().slice(0, 16)}
                    />
                  </>
                )}
              </div>

              <div style={{ marginTop: 14, padding: 12, border: "1px solid #eee", borderRadius: 8, background: "#fafafa" }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                  title={editing?.acceptedAt ? "Enable read-only access now." : "Available after the invitation is accepted."}
                >
                  <input
                    type="checkbox"
                    checked={accessEnabled}
                    onChange={(e) => handleAccessToggle(e.target.checked)}
                    disabled={!editing?.acceptedAt || accessBusy}
                  />
                  <span><strong>Allow user access to the account now</strong></span>
                </label>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                  {editing?.acceptedAt
                    ? "Toggle to immediately enable/disable this manager’s read-only access (no email re-sent)."
                    : "This becomes available after the invite is accepted."}
                </div>
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="btn" onClick={handleSendLoginLink} disabled={!editing?.acceptedAt}>
                    Send login link
                  </button>
                </div>
              </div>
            </div>

            <div className="form-actions" style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-main" type="submit" disabled={saving}>
                {saving ? "Saving…" : (editing ? "Update" : "Save")}
              </button>
              <button className="btn btn-cancel" type="button" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showLimitModal && (
        <div className="am-limit-bg" role="dialog" aria-modal="true" onClick={() => setShowLimitModal(false)}>
          <aside className="am-limit-card" onClick={(e) => e.stopPropagation()}>
            <div className="am-limit-chip">Package limit</div>
            <h3>Account manager limit reached</h3>
            <p className="am-limit-copy">
              {limitLoading
                ? "You’ve reached the allowed number of account managers for your current package."
                : `You’ve added ${usedCount || 0} of ${limit || 0} account manager${(limit || 0) === 1 ? "" : "s"} on your plan.`}
            </p>
            <div className="am-limit-actions">
              <button
                type="button"
                className="btn btn-main"
                onClick={() => {
                  setShowLimitModal(false);
                  openUpgradeModalLink();
                }}
              >
                Upgrade package
              </button>
              <button type="button" className="btn btn-cancel" onClick={() => setShowLimitModal(false)}>
                Maybe later
              </button>
            </div>
          </aside>
        </div>
      )}


      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How Account Managers work</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button">Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <ol>
                <li><b>Invite:</b> Add a trusted person’s name & email, include an optional password hint, and send the invite.</li>
                <li><b>Accept:</b> They open the invite link, enter the one-time code (OTAC) and create a manager login.</li>
                <li><b>Enable Access:</b> After acceptance, toggle <i>“Allow user access to the account now”</i>. This writes a <code>grants</code> record to allow <b>read-only</b> access.</li>
                <li><b>Manager Login:</b> They use <i>Manager Access</i> to view your dashboard. UI editing is disabled and Security Rules block writes.</li>
                <li><b>Revoke:</b> Toggle access off anytime. The grant is disabled instantly.</li>
              </ol>
              <p><b>Tip:</b> If a manager can’t see your data, check the invitation shows <i>Accepted</i> and that <i>Access enabled</i> is on.</p>
            </div>
          </aside>
        </div>
      )}

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/bk96CH5Kgfs?si=IJ2NQStfdfB4Bxqg"
        title="How it works (video)"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      <style>{`
        .am-help-row { display: flex; gap: 10px; flex-wrap: wrap; }
        .am-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .am-help.read { background: #650202; color: #fff; }
        .am-help.see  { background: #003353; color: #fff; }
        .am-help-ico { font-size: 1.1rem; }
        .am-list { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .am-item { display: flex; align-items: center; gap: 12px; padding: 12px; }
        .am-item-main { flex: 1; display: flex; align-items: center; gap: 12px; }
        .am-item-actions { display: flex; gap: 8px; }
        @media (max-width: 700px) { .am-item { flex-direction: column; align-items: stretch; } .am-item-actions { justify-content: flex-end; } }
        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}
        .am-limit-bg{position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:14000;display:flex;align-items:flex-end;justify-content:center;padding:16px;animation:amFadeIn .18s ease-out forwards}
        .am-limit-card{background:#fff;border-radius:18px;border:1px solid #e5e7eb;box-shadow:0 24px 60px rgba(15,23,42,0.35);width:min(520px,95vw);padding:18px;transform:translateY(16px);animation:amSlideUp .18s ease-out forwards}
        .am-limit-card h3{margin:6px 0 10px;color:#111827;font-size:20px}
        .am-limit-chip{display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border-radius:999px;background:#eef2ff;color:#312e81;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.03em}
        .am-limit-copy{margin:0 0 12px;color:#374151;line-height:1.5}
        .am-limit-actions{display:flex;gap:10px;flex-wrap:wrap}
        @keyframes amSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes amFadeIn{from{opacity:0}to{opacity:1}}
      `}</style>
    </>
  );
}
